import React, { useState, useMemo } from 'react';
import { theme } from '../../styles/theme';
import { PLAYER_BUILDS, PlayerBuild } from '../../data/builds';
import { CARD_TEMPLATES } from '../../data/cards';
import { PASSIVE_TEMPLATES } from '../../data/passives';
import { CardCategory } from '../../types/game';

const CATEGORY_COLORS: Record<CardCategory, string> = {
  movement: '#66ccff',
  intel: '#aa88ff',
  tactics: '#ffaa44',
  combat: '#ff5555',
  fieldcraft: '#44cc88',
  junk: '#777777',
};

interface BuildSelectScreenProps {
  onSelect: (build: PlayerBuild | 'freestyle') => void;
  onBack: () => void;
}

type Selection = PlayerBuild | 'freestyle' | null;

export function BuildSelectScreen({ onSelect, onBack }: BuildSelectScreenProps) {
  const [selected, setSelected] = useState<Selection>(null);

  // Build deck preview for the selected preset
  const deckPreview = useMemo(() => {
    if (!selected || selected === 'freestyle') return null;
    // Group by template key for count display
    const counts = new Map<string, number>();
    for (const key of selected.cardKeys) {
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return Array.from(counts.entries()).map(([key, count]) => {
      const tpl = CARD_TEMPLATES[key];
      return {
        key,
        count,
        name: tpl?.name ?? key,
        category: tpl?.category as CardCategory,
        apCost: tpl?.apCost ?? 0,
        movementRange: tpl?.movementRange,
        description: tpl?.description ?? '',
      };
    });
  }, [selected]);

  const selectedPassive = useMemo(() => {
    if (!selected || selected === 'freestyle') return null;
    return PASSIVE_TEMPLATES[selected.passiveKey] ?? null;
  }, [selected]);

  return (
    <div style={styles.container}>
      {/* Hover tooltip on preview cards — reveals the card's description so
          the player can evaluate what each card actually does before picking
          a build. Pattern mirrors DeckDraftScreen's .draft-card-tooltip. */}
      <style>{`
        .build-preview-card { position: relative; }
        .build-preview-card .build-preview-tip { display: none; }
        .build-preview-card:hover .build-preview-tip { display: block; }
      `}</style>
      <div style={styles.page}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.classification}>TOP SECRET // OPERATIVE SELECTION</div>
          <h1 style={styles.title}>SELECT BUILD</h1>
        </div>

        {/* Build options */}
        <div style={styles.grid}>
          {PLAYER_BUILDS.map(build => {
            const isSelected = selected !== 'freestyle' && selected?.id === build.id;
            const passiveInfo = PASSIVE_TEMPLATES[build.passiveKey];
            return (
              <button
                key={build.id}
                style={{
                  ...styles.buildCard,
                  borderColor: isSelected ? theme.colors.accentGreen : theme.colors.border,
                  background: isSelected ? 'rgba(0, 255, 136, 0.06)' : 'rgba(255,255,255,0.02)',
                }}
                onClick={() => setSelected(build)}
              >
                <div style={styles.buildCodename}>{build.codename}</div>
                <div style={styles.buildSubtitle}>{build.subtitle}</div>
                <div style={styles.buildDesc}>{build.description}</div>
                {passiveInfo && (
                  <div style={styles.buildPassive}>
                    <span style={{ color: theme.colors.accentGreen }}>●</span> {passiveInfo.name}
                  </div>
                )}
                <div style={styles.buildCardCount}>{build.cardKeys.length} cards</div>
              </button>
            );
          })}

          {/* Freestyle option */}
          <button
            style={{
              ...styles.buildCard,
              borderColor: selected === 'freestyle' ? theme.colors.accentBlue : theme.colors.border,
              background: selected === 'freestyle' ? 'rgba(68, 170, 255, 0.06)' : 'rgba(255,255,255,0.02)',
            }}
            onClick={() => setSelected('freestyle')}
          >
            <div style={{ ...styles.buildCodename, color: theme.colors.accentBlue }}>FREESTYLE</div>
            <div style={styles.buildSubtitle}>Custom Draft</div>
            <div style={styles.buildDesc}>
              Draft your own deck from random card pools. Pick 1 movement, 2 intel, 2 tactics, and 1 fieldcraft card. Then choose a starter passive.
            </div>
            <div style={{ ...styles.buildCardCount, color: theme.colors.accentBlue }}>6-step draft</div>
          </button>
        </div>

        {/* Deck preview — shows when a preset build is selected */}
        {deckPreview && selected !== 'freestyle' && (
          <div style={styles.previewSection}>
            <div style={styles.previewTitle}>DECK — {(selected as PlayerBuild).codename}</div>
            <div style={styles.previewCards}>
              {deckPreview.map(({ key, count, name, category, apCost, movementRange, description }) => {
                const catColor = CATEGORY_COLORS[category] ?? '#888';
                return (
                  <div key={key} className="build-preview-card" style={{ ...styles.previewCard, borderColor: catColor + '40' }}>
                    <div style={{ position: 'absolute', top: '5px', left: '7px', fontSize: '12px', fontWeight: 700, color: theme.colors.accentYellow }}>{apCost}</div>
                    {count > 1 && (
                      <div style={{ position: 'absolute', top: '5px', right: '7px', fontSize: '9px', fontWeight: 700, color: theme.colors.textSecondary }}>×{count}</div>
                    )}
                    <div style={{ fontSize: '10px', fontWeight: 600, color: theme.colors.textPrimary, textAlign: 'center', padding: '0 4px' }}>{name}</div>
                    <div style={{ fontSize: '7px', letterSpacing: '1.5px', color: catColor }}>{category.toUpperCase()}</div>
                    {movementRange && (
                      <div style={{ position: 'absolute', bottom: '5px', right: '6px', fontSize: '8px', fontWeight: 600, color: catColor }}>{movementRange}m</div>
                    )}
                    {description && (
                      <div className="build-preview-tip" style={{
                        position: 'absolute',
                        bottom: 'calc(100% + 8px)',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: 'rgba(8, 12, 20, 0.98)',
                        border: `1px solid ${catColor}80`,
                        borderRadius: '6px',
                        padding: '10px 12px',
                        minWidth: '220px',
                        maxWidth: '280px',
                        zIndex: 100,
                        pointerEvents: 'none',
                        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.6)',
                      }}>
                        <div style={{
                          fontFamily: theme.fonts.mono, fontSize: '11px', fontWeight: 700,
                          color: catColor, letterSpacing: '1px', marginBottom: '2px',
                        }}>{name}</div>
                        <div style={{
                          fontFamily: theme.fonts.mono, fontSize: '8px', color: catColor,
                          letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '6px',
                        }}>{category} · {apCost} AP{movementRange ? ` · ${movementRange}m` : ''}</div>
                        <div style={{
                          fontFamily: theme.fonts.mono, fontSize: '10px',
                          color: theme.colors.textPrimary, lineHeight: '1.5', whiteSpace: 'normal',
                        }}>{description}</div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {selectedPassive && (
              <div style={styles.previewPassive}>
                <span style={{ color: theme.colors.accentGreen, fontWeight: 700, letterSpacing: '2px', fontSize: '9px' }}>PASSIVE: {selectedPassive.name.toUpperCase()}</span>
                <span style={{ color: theme.colors.textDim, fontSize: '9px', marginLeft: '10px' }}>{selectedPassive.description}</span>
              </div>
            )}
          </div>
        )}

        {/* Freestyle preview */}
        {selected === 'freestyle' && (
          <div style={styles.previewSection}>
            <div style={{ ...styles.previewTitle, color: theme.colors.accentBlue }}>FREESTYLE DRAFT</div>
            <div style={{ fontSize: '11px', color: theme.colors.textSecondary, lineHeight: '1.6' }}>
              Start with 5 Move + 1 Improvised Strike. Draft 6 more cards across 6 rounds:
              1 Movement, 2 Intel, 2 Tactics, 1 Fieldcraft. Then pick 1 starter passive.
            </div>
          </div>
        )}

        {/* Bottom bar */}
        <div style={styles.bottomBar}>
          <button style={styles.backButton} onClick={onBack}>← MENU</button>
          <button
            style={{
              ...styles.continueButton,
              opacity: selected ? 1 : 0.3,
              cursor: selected ? 'pointer' : 'default',
            }}
            onClick={selected ? () => onSelect(selected) : undefined}
          >
            CONTINUE
          </button>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    position: 'fixed', inset: 0,
    background: 'rgba(8, 12, 20, 0.98)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontFamily: theme.fonts.mono, overflow: 'auto',
  },
  page: {
    width: '100%', maxWidth: '960px', maxHeight: '95vh',
    display: 'flex', flexDirection: 'column', overflow: 'auto', padding: '32px 24px',
  },
  header: { textAlign: 'center', marginBottom: '28px' },
  classification: {
    fontSize: '9px', letterSpacing: '4px', color: '#ff5050', fontWeight: 700, marginBottom: '8px',
  },
  title: {
    fontSize: '28px', fontWeight: 700, letterSpacing: '8px', color: theme.colors.textPrimary, margin: 0,
  },

  // Build grid
  grid: {
    display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px',
  },
  buildCard: {
    display: 'flex', flexDirection: 'column', gap: '6px',
    padding: '16px 14px', border: `1px solid ${theme.colors.border}`,
    borderRadius: '6px', cursor: 'pointer', fontFamily: theme.fonts.mono,
    textAlign: 'left', transition: 'all 0.15s', minHeight: '180px',
  },
  buildCodename: {
    fontSize: '13px', fontWeight: 700, letterSpacing: '3px', color: theme.colors.accentGreen,
  },
  buildSubtitle: {
    fontSize: '9px', letterSpacing: '1px', color: theme.colors.textSecondary, fontWeight: 600,
  },
  buildDesc: {
    fontSize: '9px', color: theme.colors.textDim, lineHeight: '1.5', flex: 1,
  },
  buildPassive: {
    fontSize: '9px', color: theme.colors.textSecondary, marginTop: '4px',
  },
  buildCardCount: {
    fontSize: '9px', letterSpacing: '1px', color: theme.colors.textDim, fontWeight: 600,
  },

  // Deck preview
  previewSection: {
    marginTop: '24px', padding: '16px',
    background: 'rgba(255,255,255,0.02)', border: `1px solid ${theme.colors.border}`,
    borderRadius: '6px',
  },
  previewTitle: {
    fontSize: '10px', letterSpacing: '3px', fontWeight: 700,
    color: theme.colors.accentGreen, marginBottom: '12px',
  },
  previewCards: {
    display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center',
  },
  previewCard: {
    width: '80px', height: '100px', position: 'relative',
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    gap: '3px', background: 'rgba(12, 16, 24, 0.92)',
    border: '1px solid rgba(30, 42, 58, 0.6)', borderRadius: '5px',
  },
  previewPassive: {
    marginTop: '12px', padding: '8px 10px',
    background: 'rgba(0, 255, 136, 0.04)', borderRadius: '4px',
    border: '1px solid rgba(0, 255, 136, 0.15)',
  },

  // Bottom
  bottomBar: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginTop: '28px', paddingTop: '20px', borderTop: `1px solid ${theme.colors.border}`,
  },
  backButton: {
    background: 'none', border: `1px solid ${theme.colors.border}`,
    color: theme.colors.textSecondary, fontFamily: theme.fonts.mono,
    fontSize: '10px', letterSpacing: '1px', padding: '8px 16px',
    cursor: 'pointer', borderRadius: '3px',
  },
  continueButton: {
    background: 'rgba(68, 204, 136, 0.08)', border: '1px solid rgba(68, 204, 136, 0.5)',
    color: '#44cc88', padding: '12px 32px', fontSize: '13px', fontWeight: 700,
    letterSpacing: '3px', cursor: 'pointer', fontFamily: theme.fonts.mono,
    borderRadius: '4px', transition: 'all 0.15s',
  },
};
